/**
 * Desktop drag & drop — HTML5 DnD with a hand-built ghost
 * (INVENTORY-DESKTOP §5; prototype `ghostDragStart` 1614-1631,
 * `componentDidMount` 1202-1219, and the byte-identical `r.over` hit-tests at
 * 2274-2303 / 2340-2369).
 *
 * `lib/drag.ts` supplies NOTHING here (§5.6): it is a *pointer* state machine
 * (330ms long-press, 9px slop, `navigator.vibrate`, `touchmove` blocking, a 1-D
 * index-based `dropIndex`, a React-mounted ghost, 450ms tap suppression). HTML5
 * drag has no long-press, no slop, no touch conflict and fires no synthetic
 * click after a drop, so none of that applies.
 *
 * WHAT LIVES HERE
 *   pure geometry   `bucketLines` · `beforeIdAt` · `windowScrollDeltaDesktop`
 *   pure adapter    `beforeIdToIdx`  ← the one correctness-critical conversion
 *   DOM read        `collectCapRects`
 *   imperative      `startGhostDrag` · `moveGhost` · `destroyGhost` and the
 *                   window listeners + auto-scroll loop they own
 *
 * `overId` IS A GAME ID, NOT AN INDEX. The prototype's drop handlers take a
 * `beforeId` ("insert before this game", `0` = append) while the store's
 * existing `setIntAt(id, v, idx)` / `placeTierAt(id, t, idx)` take an index —
 * `beforeIdToIdx` is the bridge, and it is where ordering can silently corrupt
 * (§10.5 risk 3), which is why it has its own tests.
 *
 * LANE D3 USAGE
 *
 *   // once, in the board screen
 *   useEffect(() => onDragStop(() => dispatch({ type: 'dragEnd' })), []);
 *
 *   // on a capsule
 *   <div data-gid={g.key} draggable
 *        onDragStart={(ev) => { startGhostDrag(ev, ghostOf(g)); dispatch({type:'dragStart', id:g.id}); }}
 *        onDragEnd={() => dispatch({ type: 'dragEnd' })} />
 *
 *   // on a row
 *   onDragOver={(ev) => {
 *     ev.preventDefault();
 *     if (!dragId) return;
 *     const before = beforeIdAt(collectCapRects(ev.currentTarget, dragId), ev.clientX, ev.clientY);
 *     dispatch({ type: 'over', row: rowKey, before });
 *   }}
 *   onDrop={(ev) => {
 *     ev.preventDefault();
 *     const id = dragId || parseGid(ev.dataTransfer.getData('text/plain'));
 *     void setIntAt(id, rowVote, beforeIdToIdx(rowIds, id, overBeforeId));
 *   }}
 *
 * The drag state itself (`dragId` / `overBeforeId` / `overRow`) belongs in the
 * screen's local `useReducer`, NOT the store: the store's `DragState`/`DragOver`
 * are index-based and mobile-specific (§5.6).
 */

import type { DragEvent as ReactDragEvent } from 'react';

import type { GameId } from '../lib/types.ts';
import { cancelHoverTilt } from './useHoverTilt.ts';

// ---------------------------------------------------------------------------
// constants — prototype literals
// ---------------------------------------------------------------------------

/** ghost width; the cover inside it is 114×171 (2px of border) */
export const GHOST_W = 116;
export const GHOST_COVER_W = 114;
export const GHOST_COVER_H = 171;
/** pointer sits at this offset inside the ghost — mobile used (34,108) + a -3deg rotation */
export const GHOST_OFFSET_X = 59;
export const GHOST_OFFSET_Y = 96;
/** rows wrap, so the hit-test buckets capsules into visual lines by `top` */
export const LINE_TOL = 12;
/** window auto-scroll band at either viewport edge (mobile: 130 / 110) */
export const SCROLL_EDGE_PX = 120;
/** window auto-scroll max velocity, px per frame (mobile: 12) */
export const SCROLL_MAX_PX = 28;
/** 1×1 transparent GIF — `setDragImage` target, kills the browser's own ghost */
export const EMPTY_GIF = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

/** `0` is the prototype's "no target — append to the end of the row". */
export type BeforeId = GameId | 0;

// ---------------------------------------------------------------------------
// slot geometry — the wrapping row's landing slot (2026-08-28)
// ---------------------------------------------------------------------------

/**
 * A wrapping row's resting slot lattice: how many capsules fit on a line, and
 * the pitch between adjacent slots across and down.
 *
 * WHY THIS EXISTS. The prototype opened the landing slot by giving the target
 * capsule `margin-left: 124px` (a capsule + its gap) and collapsing the dragged
 * one to zero width. On mobile that is exact: the row is ONE non-wrapping
 * horizontal scroller, so the 124px it frees and the 124px it consumes are the
 * same line's, and the row's shape never changes.
 *
 * Desktop rows WRAP (`flexWrap:'wrap'`), and there the two halves land on
 * different lines whenever the drag crosses one. The freed line packs an extra
 * capsule up, the consumed line pushes one down, and the row re-flows into a
 * different number of lines MID-DRAG — the capsule the pointer was aimed at
 * moves a whole line away, and the rows below get shoved down out from under
 * the cursor. Measured on an 18-game row at 9 per line: 9+9 at rest became
 * 8+9+1 the moment a line was crossed, which is the whole of "I can only drag
 * the first few games, and I cannot drop on the lower rows at all".
 *
 * The fix keeps the affordance and drops the cause: within one row the layout
 * is FROZEN (every capsule keeps its box, so the lattice cannot change) and the
 * slot is opened by TRANSFORMS, which do not participate in flex layout. This
 * lattice is what turns "capsule 12 now sits in slot 8" into the pixels.
 */
export interface SlotGrid {
  /** capsules per line at rest; `0` when the row could not be measured */
  perLine: number;
  /** centre-to-centre distance between adjacent slots on a line */
  pitchX: number;
  /** centre-to-centre distance between lines */
  pitchY: number;
}

export const NO_GRID: SlotGrid = { perLine: 0, pitchX: 0, pitchY: 0 };

/**
 * The lattice of a row, read from its capsules' resting rects.
 *
 * `perLine` is the first line's population, `pitchX` the step between its first
 * two members, `pitchY` the step to the first member of the second line. A row
 * with a single line has no `pitchY` to measure and does not need one — nothing
 * in it can wrap.
 */
export function slotGridOf(rects: readonly CapRect[], tol: number = LINE_TOL): SlotGrid {
  if (rects.length < 2) return NO_GRID;
  const lines = bucketLines(rects, tol);
  const first = lines[0];
  if (!first || first.items.length < 2) return NO_GRID;
  const a = first.items[0];
  const b = first.items[1];
  if (!a || !b) return NO_GRID;
  const pitchX = b.left - a.left;
  if (pitchX <= 0) return NO_GRID;
  const second = lines[1];
  const nextTop = second?.items[0]?.top;
  const pitchY = nextTop === undefined ? 0 : nextTop - a.top;
  return { perLine: first.items.length, pitchX, pitchY };
}

/**
 * Where natural slot `idx` ends up once the capsule at `from` is moved to sit
 * at `to`, both indices into the row's resting order.
 *
 * Everything between the two shifts by exactly one slot — towards `from` when
 * the capsule travels forward, away from it when it travels back. This is the
 * permutation the drop will actually perform, which is the point: the preview
 * and the result are the same arrangement, so nothing jumps at drop.
 */
export function slotIndexAfterMove(from: number, to: number, idx: number): number {
  if (from < 0 || to < 0 || from === to) return idx;
  if (idx === from) return to;
  if (from < to) return idx > from && idx <= to ? idx - 1 : idx;
  return idx >= to && idx < from ? idx + 1 : idx;
}

/**
 * The `transform` that carries the capsule at natural slot `idx` to the slot it
 * occupies during the move, or `''` when it does not move.
 *
 * Wrap-correct by construction: a slot's position is derived from the lattice
 * (`col = i % perLine`, `line = i / perLine`), so a capsule that shifts across a
 * line boundary travels back along the row and down a line, exactly as it will
 * sit after the drop. A pure `translateX` could not express that, which is why
 * the margin it replaces could not either.
 */
export function slotShiftTransform(from: number, to: number, idx: number, grid: SlotGrid): string {
  if (grid.perLine <= 0) return '';
  const next = slotIndexAfterMove(from, to, idx);
  if (next === idx) return '';
  const col = (i: number): number => i % grid.perLine;
  const line = (i: number): number => Math.floor(i / grid.perLine);
  const dx = (col(next) - col(idx)) * grid.pitchX;
  const dy = (line(next) - line(idx)) * grid.pitchY;
  if (!dx && !dy) return '';
  return 'translate(' + dx + 'px,' + dy + 'px)';
}

// ---------------------------------------------------------------------------
// pure geometry
// ---------------------------------------------------------------------------

/** One draggable capsule, reduced to what the hit-test needs. */
export interface CapRect {
  gid: GameId;
  top: number;
  bottom: number;
  left: number;
  width: number;
}

export interface CapLine {
  top: number;
  bottom: number;
  items: CapRect[];
}

/**
 * Group capsules into visual lines. A wrapping flex row has no DOM signal for
 * "line", so the prototype buckets by `|line.top - rect.top| < 12`; each line's
 * `bottom` grows to the tallest member. Input order is DOM order, which for a
 * wrapping row is also visual order, so the returned lines are top-to-bottom.
 */
export function bucketLines(items: readonly CapRect[], tol: number = LINE_TOL): CapLine[] {
  const lines: CapLine[] = [];
  for (const it of items) {
    const ln = lines.find((l) => Math.abs(l.top - it.top) < tol);
    if (ln) {
      ln.items.push(it);
      ln.bottom = Math.max(ln.bottom, it.bottom);
    } else {
      lines.push({ top: it.top, bottom: it.bottom, items: [it] });
    }
  }
  return lines;
}

/**
 * The insertion target under the pointer, as a **game id** ("insert before
 * this one"); `0` means append.
 *
 * Rules, verbatim from the prototype:
 *   - pick the first line whose `bottom` is at or below the pointer;
 *   - inside it, the first capsule whose horizontal midpoint the pointer has
 *     not yet passed;
 *   - if the pointer is past every capsule on that line, fall through to the
 *     FIRST capsule of the next line (so the gap opens at the wrap point);
 *   - past the last line entirely → `0` (append).
 */
export function beforeIdAt(items: readonly CapRect[], x: number, y: number, tol: number = LINE_TOL): BeforeId {
  if (!items.length) return 0;
  const lines = bucketLines(items, tol);
  const li = lines.findIndex((l) => y <= l.bottom);
  if (li < 0) return 0;
  const line = lines[li];
  if (!line) return 0;
  const hit = line.items.find((it) => x < it.left + it.width / 2);
  if (hit) return hit.gid;
  const nextLine = lines[li + 1];
  const first = nextLine ? nextLine.items[0] : undefined;
  return first ? first.gid : 0;
}

/**
 * `beforeId` → the index the store's `setIntAt` / `placeTierAt` expect.
 *
 * The prototype rebuilt a FLAT order array, removed the dragged id and spliced
 * it before `beforeId`. Our row-scoped equivalent is identical because
 * `beforeId` is always a member of the target row: inserting before it in the
 * flat array inserts before it within the row too.
 *
 * `rowIds` is the target row's current order (`intRowIds` / `tierRowIds`).
 * A `beforeId` of `0`, or one that is not in the row (filtered out, stale),
 * appends — matching the prototype's `push` fallback.
 */
export function beforeIdToIdx(rowIds: readonly GameId[], dragId: GameId, beforeId: BeforeId): number {
  const rest = rowIds.filter((x) => x !== dragId);
  if (!beforeId) return rest.length;
  const i = rest.indexOf(beforeId);
  return i < 0 ? rest.length : i;
}

/**
 * Window auto-scroll for one frame: negative scrolls up, positive down, `0` is
 * outside both bands. Prototype: 120px band, 28px/frame max, `Math.ceil`.
 * There is no row-level auto-scroll on desktop — rows wrap, they never scroll.
 */
export function windowScrollDeltaDesktop(
  y: number,
  innerHeight: number,
  edge: number = SCROLL_EDGE_PX,
  maxV: number = SCROLL_MAX_PX,
): number {
  if (y < 0) return 0;
  if (y < edge) return -Math.ceil(((edge - y) / edge) * maxV);
  if (y > innerHeight - edge) return Math.ceil(((y - (innerHeight - edge)) / edge) * maxV);
  return 0;
}

/**
 * `data-gid` back to a `GameId`. The attribute is written from `CapsuleVM.key`
 * (`'' + g.id`), and ids are `number | string` — CheapShark rows carry `cs1234`.
 * The prototype used `parseInt`, which silently dropped those; round-tripping
 * through `String` keeps them addressable. Returns `null` for junk.
 */
export function parseGid(raw: string | null | undefined): GameId | null {
  if (!raw) return null;
  const n = Number(raw);
  if (Number.isFinite(n) && String(n) === raw) return n === 0 ? null : n;
  return raw;
}

// ---------------------------------------------------------------------------
// DOM read
// ---------------------------------------------------------------------------

/**
 * Every `[data-gid]` inside `container`, minus the dragged capsule and minus
 * anything narrower than 10px — that width filter is how the prototype skips
 * the collapsed source capsule (`capW: '0px'` while it is being dragged).
 */
export function collectCapRects(container: Element, dragId: GameId | 0, minWidth = 10): CapRect[] {
  const out: CapRect[] = [];
  const els = container.querySelectorAll('[data-gid]');
  for (const el of Array.from(els)) {
    const gid = parseGid(el.getAttribute('data-gid'));
    if (gid === null || gid === dragId) continue;
    const r = el.getBoundingClientRect();
    if (r.width < minWidth) continue;
    out.push({ gid, top: r.top, bottom: r.bottom, left: r.left, width: r.width });
  }
  return out;
}

/**
 * Every capsule in `container`, in DOM order, positioned RELATIVE to the
 * container's border box — the row's resting lattice, captured once.
 *
 * Two reasons it is not `collectCapRects`:
 *
 *  1. **Relative, so scrolling cannot stale it.** A drag auto-scrolls, which
 *     moves every viewport rect; a row-relative rect does not move, so one
 *     capture stays true for the whole drag.
 *  2. **Nothing is filtered.** The dragged capsule holds its box in a frozen
 *     row and is a real slot in the lattice; dropping it would shorten the row
 *     by one and put every pitch out by a slot.
 *
 * Read BEFORE any shift is applied. `getBoundingClientRect` includes
 * transforms, so re-reading a row mid-drag returns the shifted positions — and
 * feeding those back into the hit-test that produced them is exactly the
 * oscillation this whole mechanism exists to avoid.
 */
export function collectSlotRects(container: Element): CapRect[] {
  const base = container.getBoundingClientRect();
  const out: CapRect[] = [];
  for (const el of Array.from(container.querySelectorAll('[data-gid]'))) {
    const gid = parseGid(el.getAttribute('data-gid'));
    if (gid === null) continue;
    const r = el.getBoundingClientRect();
    out.push({
      gid,
      top: r.top - base.top,
      bottom: r.bottom - base.top,
      left: r.left - base.left,
      width: r.width,
    });
  }
  return out;
}

/**
 * `r.leave` — a dragleave that merely crossed into a child is not a leave.
 */
export function isRealDragLeave(currentTarget: Element, relatedTarget: EventTarget | null): boolean {
  if (relatedTarget instanceof Node && currentTarget.contains(relatedTarget)) return false;
  return true;
}

// ---------------------------------------------------------------------------
// the imperative ghost + window machinery
// ---------------------------------------------------------------------------

/** What the ghost needs to draw itself — a slice of `TileVM`. */
export interface GhostVM {
  id: GameId;
  /** UPPERCASE name, as the prototype renders it */
  nameUp: string;
  /** resolved CSS background for the cover, or '' to use `fall` */
  coverBg?: string | undefined;
  /** the fallback gradient (`TileVM.fall`) */
  fall: string;
}

let ghostEl: HTMLDivElement | null = null;
let emptyImg: HTMLImageElement | null = null;
let dragY = -1;
let scrollRaf = 0;
let wired = false;
const stopSubs = new Set<() => void>();

/**
 * Subscribe to "the drag is over" (dragend OR drop, whichever fires first).
 * Returns an unsubscribe. The board screens use this to clear their local
 * `dragId` / `overRow` / `overBeforeId` even when the drop lands outside a row.
 */
export function onDragStop(cb: () => void): () => void {
  stopSubs.add(cb);
  return () => {
    stopSubs.delete(cb);
  };
}

/** Move the ghost to the pointer. Exported for tests; the loop calls it. */
export function moveGhost(clientX: number, clientY: number): void {
  if (!ghostEl) return;
  ghostEl.style.transform =
    'translate(' + (clientX - GHOST_OFFSET_X) + 'px,' + (clientY - GHOST_OFFSET_Y) + 'px)';
}

/** Remove the body-appended ghost node, if any. Safe to call at any time. */
export function destroyGhost(): void {
  if (!ghostEl) return;
  try {
    ghostEl.remove();
  } catch {
    /* detached already */
  }
  ghostEl = null;
}

function onWindowDragOver(ev: DragEvent): void {
  dragY = ev.clientY;
  moveGhost(ev.clientX, ev.clientY);
}

function scrollStep(): void {
  const d = windowScrollDeltaDesktop(dragY, window.innerHeight);
  if (d) window.scrollBy(0, d);
  scrollRaf = requestAnimationFrame(scrollStep);
}

/**
 * Tear down everything a drag owns. Prototype `_onDragStop` (line 1207) did the
 * same minus the rAF — its `dragScroll` loop ran for the whole session
 * (§9 item 9); ours starts on drag start and stops here.
 */
function stopDrag(): void {
  dragY = -1;
  destroyGhost();
  if (scrollRaf) {
    cancelAnimationFrame(scrollRaf);
    scrollRaf = 0;
  }
  if (wired) {
    window.removeEventListener('dragover', onWindowDragOver);
    window.removeEventListener('dragend', stopDrag);
    window.removeEventListener('drop', stopDrag);
    wired = false;
  }
  for (const cb of Array.from(stopSubs)) cb();
}

/** Public teardown — DesktopApp calls it on unmount (breakpoint switch). */
export function endDesktopDrag(): void {
  stopDrag();
}

function buildGhost(vm: GhostVM, clientX: number, clientY: number): HTMLDivElement {
  const n = document.createElement('div');
  n.setAttribute('data-qd-drag-ghost', '');
  n.style.cssText =
    'position:fixed;top:0;left:0;width:' +
    GHOST_W +
    'px;background:var(--gt-tile);border:1px solid var(--gt-border-2);pointer-events:none;z-index:9999;' +
    'box-shadow:8px 10px 0 rgba(0,0,0,.55);transform:translate(' +
    (clientX - GHOST_OFFSET_X) +
    'px,' +
    (clientY - GHOST_OFFSET_Y) +
    'px)';
  const cov = document.createElement('div');
  cov.style.cssText =
    'width:' + GHOST_COVER_W + 'px;height:' + GHOST_COVER_H + 'px;background:' + (vm.coverBg || vm.fall);
  const nm = document.createElement('div');
  nm.style.cssText =
    'padding:5px 6px;font-family:var(--gt-font-mono);font-size:8.5px;color:var(--gt-text-3);' +
    'white-space:nowrap;overflow:hidden;text-overflow:ellipsis;background:var(--gt-tile)';
  nm.textContent = vm.nameUp;
  n.appendChild(cov);
  n.appendChild(nm);
  return n;
}

/**
 * `onDragStart` for any draggable capsule / cover.
 *
 * Sets the payload, suppresses the browser's own drag image with a 1×1
 * transparent GIF, builds the body-level ghost, kills any in-flight hover tilt
 * and arms the window listeners + auto-scroll loop. Everything is wrapped the
 * way the prototype wrapped it: a `dataTransfer` that throws (Firefox in some
 * modes) must not abort the drag, because `dragId` in React state is the real
 * source of truth and `dataTransfer` is only the fallback (§10.5 risk 2).
 */
export function startGhostDrag(ev: ReactDragEvent<Element> | DragEvent, vm: GhostVM): void {
  const dt = ev.dataTransfer;
  try {
    if (dt) {
      dt.setData('text/plain', String(vm.id));
      dt.effectAllowed = 'move';
      if (typeof dt.setDragImage === 'function') {
        if (!emptyImg) {
          emptyImg = new Image();
          emptyImg.src = EMPTY_GIF;
        }
        dt.setDragImage(emptyImg, 0, 0);
      }
    }
  } catch {
    /* dataTransfer is unavailable — React state still carries the drag id */
  }
  try {
    destroyGhost();
    ghostEl = buildGhost(vm, ev.clientX, ev.clientY);
    document.body.appendChild(ghostEl);
  } catch {
    ghostEl = null;
  }
  cancelHoverTilt();
  dragY = ev.clientY;
  if (!wired) {
    window.addEventListener('dragover', onWindowDragOver);
    window.addEventListener('dragend', stopDrag);
    window.addEventListener('drop', stopDrag);
    wired = true;
  }
  if (!scrollRaf) scrollRaf = requestAnimationFrame(scrollStep);
}

/** Test seam. */
export function resetDragHtml5Module(): void {
  stopDrag();
  stopSubs.clear();
  emptyImg = null;
}
